import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  EmailTemplate,
  EmailTemplatesService,
  Blueprint,
  tagClassName,
  displayTag,
} from "../services/emailTemplatesService";

const ChooseTemplate: React.FC = () => {
  const navigate = useNavigate();

  const [blueprints, setBlueprints] = useState<Blueprint[]>([]);
  const [galleryTemplates, setGalleryTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"lifecycle" | "newsletter">("lifecycle");

  useEffect(() => {
    Promise.all([
      EmailTemplatesService.getBlueprints(),
      EmailTemplatesService.getTemplates(),
    ])
      .then(([bps, gallery]) => {
        setBlueprints(bps);
        setGalleryTemplates(gallery);
      })
      .catch(() => setError("Failed to load templates."))
      .finally(() => setLoading(false));
  }, []);

  const handleBuildFromBlueprint = async (bp: Blueprint) => {
    setBusyId(`blueprint-${bp.id}`);
    setError(null);
    try {
      const result = await EmailTemplatesService.buildEdition(bp.id);
      navigate(`/newsletterEdition/${result.edition_id}`);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to create post from this template.");
      setBusyId(null);
    }
  };

  const handleImportTemplate = async (templateId: number) => {
    setBusyId(`template-${templateId}`);
    setError(null);
    try {
      const result = await EmailTemplatesService.importTemplate(templateId);
      const editionId = activeTab === "lifecycle"
        ? result.lifecycle_edition_id
        : result.newsletter_edition_id;
      navigate(`/newsletterEdition/${editionId}`);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to import template.");
      setBusyId(null);
    }
  };

  const usableBlueprints = blueprints.filter(
    (bp) => bp.has_default_template && bp.blueprint_type === activeTab
  );

  if (loading) {
    return (
      <div className="templates-page">
        <p className="templates-status">Loading...</p>
      </div>
    );
  }

  return (
    <div className="templates-page">
      <div className="templates-page-header">
        <div>
          <h1>Create new post</h1>
          <p style={{ marginTop: 0 }}>
            Choose a template to get started, or open a blank editor.
          </p>
        </div>
      </div>

      {error && (
        <p style={{ color: "#dc2626", marginBottom: "16px" }}>{error}</p>
      )}

      <div className="blueprint-tabs" style={{ marginTop: 0, marginBottom: "24px" }}>
        <button
          className={`blueprint-tab${activeTab === "lifecycle" ? " active" : ""}`}
          onClick={() => setActiveTab("lifecycle")}
        >
          Lifecycle email
        </button>
        <button
          className={`blueprint-tab${activeTab === "newsletter" ? " active" : ""}`}
          onClick={() => setActiveTab("newsletter")}
        >
          Newsletter
        </button>
      </div>

      <p style={{ color: "#6b7280", fontSize: "14px", marginTop: 0, marginBottom: "24px" }}>
        {activeTab === "lifecycle"
          ? "A one-off email blast — great for welcome emails, announcements, offers, and re-engagement."
          : "A longer, multi-section post — best for recurring newsletters with curated content."}
      </p>

      {/* ── Blank editor ─────────────────────────────────── */}
      <button
        className="choose-template-scratch"
        onClick={() => navigate("/newsletterEdition/new?blank=true")}
      >
        <div className="choose-template-scratch-inner">
          <span style={{ fontSize: "28px" }}>+</span>
          <strong>Blank post</strong>
          <span style={{ fontSize: "13px", color: "#6b7280" }}>
            Start from scratch
          </span>
        </div>
      </button>

      {/* ── Your blueprint templates ─────────────────────── */}
      {usableBlueprints.length > 0 && (
        <>
          <h2 className="choose-template-section-title">Your blueprint templates</h2>
          <div className="templates-grid">
            {usableBlueprints.map((bp) => (
              <button
                key={bp.id}
                className="template-card choose-template-card-btn"
                disabled={busyId === `blueprint-${bp.id}`}
                onClick={() => handleBuildFromBlueprint(bp)}
              >
                <div className="template-card-thumbnail">
                  {bp.preview_html ? (
                    <div className="template-card-thumbnail-scaler">
                      <iframe
                        srcDoc={bp.preview_html}
                        title={bp.title}
                        sandbox="allow-same-origin"
                        className="template-card-thumbnail-iframe"
                      />
                    </div>
                  ) : (
                    <span className="template-card-thumbnail-label">
                      No preview
                    </span>
                  )}
                </div>
                <div className="template-card-body">
                  <h3 className="template-card-title">{bp.title}</h3>
                  <span className="choose-template-card-action">
                    {busyId === `blueprint-${bp.id}`
                      ? "Creating..."
                      : "Use this template"}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {/* ── Quick start templates ────────────────────────── */}
      {galleryTemplates.length > 0 && (
        <>
          <h2 className="choose-template-section-title">Quick start templates</h2>
          <div className="templates-grid">
            {galleryTemplates.map((t) => {
              const previewHtml = activeTab === "lifecycle"
                ? (t.lifecycle_preview_html || t.preview_html)
                : t.preview_html;
              return (
                <button
                  key={t.id}
                  className="template-card choose-template-card-btn"
                  disabled={busyId === `template-${t.id}`}
                  onClick={() => handleImportTemplate(t.id)}
                >
                  <div className="template-card-thumbnail">
                    {previewHtml ? (
                      <div className="template-card-thumbnail-scaler">
                        <iframe
                          srcDoc={previewHtml}
                          title={t.title}
                          sandbox="allow-same-origin"
                          className="template-card-thumbnail-iframe"
                        />
                      </div>
                    ) : (
                      <span className="template-card-thumbnail-label">
                        Preview &rarr;
                      </span>
                    )}
                  </div>
                  <div className="template-card-body">
                    <h3 className="template-card-title">{t.title}</h3>
                    {t.description && (
                      <p className="template-card-description">{t.description}</p>
                    )}
                    {t.tags.length > 0 && (
                      <div className="template-card-tags" style={{ marginTop: "4px" }}>
                        {t.tags.map((tag) => (
                          <span key={tag} className={tagClassName(tag)}>
                            {displayTag(tag)}
                          </span>
                        ))}
                      </div>
                    )}
                    <span className="choose-template-card-action">
                      {busyId === `template-${t.id}`
                        ? "Importing..."
                        : "Use this template"}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default ChooseTemplate;
